import { SelectFeatureTarget } from "fond/topologicalView/TopologicalViewContext";
import { LayerIds, SpliceType } from "fond/types";

type Cable = {
  id: string | null;
  label: string;
  backgroundColor: string;
};

export type FiberRange = {
  start: number;
  end: number;
};

export type ActiveCableSplice = {
  // The handle ID indicates whether an edge handle (from React Flow)
  // should be rendered against this splice.
  handleId: string | null;

  // The cable this part of the splice refers to.
  cable: Cable;

  // The range of fibers this splice represents.
  fiberRange: FiberRange;

  // The tier of the cable (e.g., "DROP", "DISTRIBUTION", etc.)
  tier: string;
};

export type SpliceRecordCable = "dead" | "spare" | ActiveCableSplice;

export type SpliceRecordLeaf = {
  id: string | null;
  label: string;
};

export type SpliceRecord = {
  // The cable/fiber on the input side of the row.
  input: SpliceRecordCable;

  // The cable/fiber on the output side of the row.
  output: SpliceRecordCable;

  // The 'service location' (if any) served by this row.
  leaf: SpliceRecordLeaf | null;

  // The type of splice happening at this row.
  spliceType: SpliceType;

  // The number of records associated with this splice range.
  recordCount: number;
};

export type TopologicalNodeData = {
  // A unique ID for this node.
  id: string;

  // The layer this node belongs to
  layer: { id: string; key: string };

  // A label for this node.
  label: string;

  // Splicing records
  spliceRecords: SpliceRecord[];

  // An estimate for the dimensions for this row.
  dimensions: { width: number; height: number };

  // A position for this node.
  position: { x: number; y: number } | null;
};

export type TopologicalEdgeData = {
  id: string | null;
  label: string;
};

/**
 * Return whether the provided SpliceRowCable is an ActiveSplice.
 */
export const isActiveSplice = (cable: SpliceRecordCable | null | undefined): cable is ActiveCableSplice => {
  return (cable && cable !== "dead" && cable !== "spare") ?? false;
};

/**
 * Return a SelectFeatureTarget for the provided cable, or null if the cable is not active.
 */
export const getCableSelectFeatureTarget = (cable: SpliceRecordCable | null | undefined): SelectFeatureTarget | null => {
  if (!isActiveSplice(cable)) {
    return null;
  }

  const { id, label } = cable.cable;
  const lookup = id ? { id } : { fondId: label };
  return { layerKey: LayerIds.fibreCable, ...lookup };
};
